import React, { useState, useEffect } from "react";
import Table from "../../../../../Components/UiComponents/Table/Table";
import { useNavigate } from "react-router-dom";
import {
  Editicon,
  Deleteicon,
  PaperClipIcon,
  EditConvgIcon,
  Viewicon,
} from "../../../../../Components/IconComponents";
import { useDispatch } from "react-redux";
import { SopInstancedata, viewtaskcolumnsForExportView } from "../../../../../JsonData/CreateTaskConfig";
import { setTableData } from "../../../../../CentralStore/ExportLMSTable/dataSlice";
import {
  downloadFile,
  getExportedColumnsData,
} from "../../../../../Components/UiComponents/utils";
import { getTaskListURL, deleteTaskURL, sopInstanceList, getThemeURL, regionListURL } from "../../../../../Services/EndPoints";
import { getData, putDataWithBody } from "../../../../../Services/Services";
import Moment from "react-moment";
import { toast } from "react-toastify";
import Confirmpopup from "../../../Common/ConfirmPopup/Confirmpopup";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { Filter as FilterIcon, Sheet, FileText, Clock, History as HistoryIcon } from "lucide-react";
import SopInstanceAuditHistoryModal from "./SopInstanceAuditHistoryModal";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// Canonical severity levels (same list as the Incident Category Matrix) so every level is always selectable.
const SEVERITY_OPTIONS = ["Level 1 – Minor", "Level 2 – Moderate", "Level 3 – Major", "Level 4 – Critical"];

// Local (not UTC) today as YYYY-MM-DD — the UTC date is a day behind for part of the day in IST.
const TODAY_STR = (() => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
})();
// Typing a future date into a native date input bypasses `max`, so clamp it here.
const clampToToday = (v) => (v && v > TODAY_STR ? TODAY_STR : v);

const SOP_INSTANCE_BASE_EXPORT_COLUMNS = [
  "SOP Instance ID",
  "Instance Name",
  "Incident Name",
  "Incident ID",
  "Severity",
  "Region",
  "Status",
  "Created By",
  "Created On",
  "Trigger Type",
  "SLA Hours",
  "Target Datetime",
  "SLA Elapsed (min)",
  "SLA Breached",
];

const exportFileStamp = () => {
  const p = (n) => String(n).padStart(2, "0");
  const n = new Date();
  return `${p(n.getDate())}-${p(n.getMonth() + 1)}-${n.getFullYear()}_${p(n.getHours())}${p(
    n.getMinutes()
  )}${p(n.getSeconds())}`;
};

// e.g. "IST (UTC+05:30)" — derived from the browser's own local timezone so
// the hover hint always matches what the Moment-rendered date/time actually is.
const TIMEZONE_LABEL = (() => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  let zoneName = "";
  try {
    zoneName =
      new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName")?.value || "";
  } catch (e) {
    zoneName = "";
  }
  return `${zoneName ? zoneName + " " : ""}(UTC${sign}${hh}:${mm})`;
})();

const STATUS_LABEL_MAP = {
  Started: "Started",
  "In-Progress": "In Progress",
  Completed: "Completed",
  Cancel: "Canceled",
  Closed: "Closed",
};

const badgeBaseStyle = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  lineHeight: 1.6,
};

// Light/pastel palette matching the Incident Category Matrix screen's
// severity badge convention (level 1 -> green, ... level 4 -> red).
const getSeverityBadgeStyle = (severity) => {
  const s = (severity || "").toLowerCase();
  let palette = { background: "#eef2f7", color: "#5b6b7a", border: "#d5e0ea" };
  if (s.includes("minor") || s.includes("level 1")) {
    palette = { background: "#e6f4ea", color: "#1e7e34", border: "#b7e1c1" };
  } else if (s.includes("moderate") || s.includes("level 2")) {
    palette = { background: "#fff4e0", color: "#b26a00", border: "#f2d9a8" };
  } else if (s.includes("major") || s.includes("level 3")) {
    palette = { background: "#ffe9d9", color: "#c2410c", border: "#f3c2a0" };
  } else if (s.includes("critical") || s.includes("level 4")) {
    palette = { background: "#fde5e5", color: "#c0392b", border: "#f2b8b8" };
  }
  return {
    ...badgeBaseStyle,
    background: palette.background,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

// Light/pastel palette for SOP instance status chips.
const STATUS_BADGE_PALETTE = {
  Started: { background: "#DBEAFE", color: "#1D4ED8", border: "#BFDBFE", dot: "#3B82F6" },
  "In-Progress": { background: "#FEF3C7", color: "#92400E", border: "#FDE68A", dot: "#F59E0B" },
  Completed: { background: "#DCFCE7", color: "#15803D", border: "#BBF7D0", dot: "#22C55E" },
  Cancel: { background: "#FEE2E2", color: "#B91C1C", border: "#FECACA", dot: "#EF4444" },
  Closed: { background: "#F1F5F9", color: "#64748B", border: "#E2E8F0", dot: "#94A3B8" },
};
const getStatusBadgeStyle = (status) => {
  const palette = STATUS_BADGE_PALETTE[status] || { background: "#F1F5F9", color: "#64748B", border: "#E2E8F0", dot: "#94A3B8" };
  return {
    ...badgeBaseStyle,
    background: palette.background,
    color: palette.color,
    border: `1px solid ${palette.border}`,
    dot: palette.dot,
  };
};

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
};

const exportBtnBase = {
  height: "34px",
  width: "34px",
  padding: 0,
  fontSize: "12px",
  fontWeight: 600,
  borderRadius: "6px",
  cursor: "pointer",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
const excelExportBtnStyle = {
  ...exportBtnBase,
  border: "1px solid #10B981",
  color: "#fff",
  background: "#10B981",
};
const pdfExportBtnStyle = {
  ...exportBtnBase,
  border: "1px solid #dc3545",
  color: "#fff",
  background: "#dc3545",
};
const exportBtnDisabledStyle = {
  ...exportBtnBase,
  border: "1px solid #e5e9ef",
  color: "#aab4bf",
  background: "#f4f6f8",
  cursor: "not-allowed",
};

const SopInstanceTable = (props) => {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [viewTypeData, setViewTypeData] = useState([]);
  const [incidentTypeData, setIncidentTypeData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [incidentBanner, setIncidentBanner] = useState(null);
  const [matchBanner, setMatchBanner] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [severityFilter, setSeverityFilter] = useState("All");
  const [regionFilter, setRegionFilter] = useState("All");
  const [regionMaster, setRegionMaster] = useState([]);
  const [triggerTypeFilter, setTriggerTypeFilter] = useState("All");
  // Date range only takes effect once "Apply" is clicked — pending values are
  // what the inputs show, applied values are what actually filters the data.
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [internalSearchText, setInternalSearchText] = useState("");
  // Search can be owned by the parent (SopInstance.js renders the search box
  // in the page header row) or, if unused, falls back to local state.
  const searchText = props.searchText !== undefined ? props.searchText : internalSearchText;
  const setSearchText = props.setSearchText || setInternalSearchText;
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);

  const highlightedIncidentName = props.locationInstance?.incidentName || "";
  // SOP instance ids (reference_id) passed from the View Incident → Quick Links
  // "SOP Executed / Completed / Cancelled / Closed" link. Matched rows are bold.
  const highlightSopIds = (props.locationInstance?.highlightSopIds || []).map((v) => String(v));
  // Task instance ids — set when arriving from the "Task In Progress / Completed"
  // link. Carried through to the SOP Instance detail view so its Task List can
  // expand + highlight the matching task rows.
  const highlightTaskIds = props.locationInstance?.highlightTaskIds || [];
 
  useEffect(() => {
    getRegionMaster();
  }, []);

  useEffect(() => {
    getTaskLists();
    // Auto-refresh the SOP instance list every 30 minutes.
    const intervalId = setInterval(() => getTaskLists(true), 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);
 
  const getShortenedText = (strLength, fulltext) => {
    let abbreviatedText = "";
    if (fulltext.length > strLength) {
      abbreviatedText = fulltext.substring(0, 25) + "...";
    } else {
      abbreviatedText = fulltext;
    }
    return <span title={fulltext}>{abbreviatedText}</span>;
  };
  // Full configured region list (same API the Create Contact / Create User forms use), so
  // every region is filterable even when no loaded SOP instance is in it yet.
  async function getRegionMaster() {
    try {
      const userProfile = await getData(getThemeURL);
      const state_id = userProfile[0]?.state_id;
      const regionList = await getData(`${regionListURL}/${state_id}`);
      setRegionMaster(
        (Array.isArray(regionList) ? regionList : [])
          .filter((r) => r.name && Number(r.parent_region_id) !== 0)
          .map((r) => String(r.name).trim().toUpperCase())
      );
    } catch (e) {
      console.error("Error fetching regions:", e);
    }
  }
  async function getTaskLists(silent = false) {
    if (!silent) setLoader(true);
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var taskList = await getData(`${sopInstanceList}/${state_id}`);
    // let sortedList = taskList.sort((a, b) => {
    //   return new Date(b.created_on) - new Date(a.created_on);
    // });
    if (!silent) setLoader(false);
    setData(taskList);
    setLastRefreshed(new Date());

    const navIncidentName = props.locationInstance?.incidentName;
    if (navIncidentName) {
      const matched = taskList.some(item => item.incident === navIncidentName);
      setIncidentBanner({ found: matched, name: navIncidentName });
    }

    if (highlightSopIds.length) {
      const matchedCount = taskList.filter(item => highlightSopIds.includes(String(item.id))).length;
      setMatchBanner({
        found: matchedCount > 0,
        count: matchedCount,
        label: highlightTaskIds.length ? "SOP" : (props.locationInstance?.sourceLabel || "SOP"),
        fromIncident: props.locationInstance?.fromIncident || "",
        taskHint: highlightTaskIds.length > 0,
      });
    }
  }
  function addNewHandler() {
    navigate("/planning/create-task");
  }
 
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = SopInstancedata;

      // Transform data to add created_by field
    const transformedData = data.map(item => ({
      ...item,
      created_by: item.created_by_lastname 
        ? `${item.created_by_firstname} ${item.created_by_lastname}`
        : item.created_by_firstname || "NA"
    }));

      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, transformedData),
        })
      );
    }
  };
 
  const columns = React.useMemo(
    () => [
      {
        Header: "SOP Instance ID",
        accessor: "sopinstance_seq_id",
        disableSortBy: false,
        Cell: (props) => (
          <span
            title="Click to view SOP Instance details"
            style={{
              color: "#146680",
              fontWeight: 700,
              cursor: "pointer",
              textDecoration: "underline dotted",
              textUnderlineOffset: "3px",
            }}
            onClick={() =>
              navigate("../view-sop-instance", {
                state: { data: props.row.original },
              })
            }
          >
            {props.value || "NA"}
          </span>
        ),
      },
      {
        Header: t("sopinstancelist.name"),
        accessor: "name",

        disableSortBy: false,
        Cell: (props) => {
          if (!props.value) return "NA";
          return (
            <span
              title={props.value}
              style={{
                display: "block",
                maxWidth: "220px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {props.value}
            </span>
          );
        },
      },
      // {
      //   Header: t("sopinstancelist.sop"),
      //   accessor: "sop",
      //   disableSortBy: true,
      // },
      {
        Header: t("sopinstancelist.incident"),
        accessor: "incident",

        Cell: (props) => {
          const incidentSeqId = props.row.original.incident_seq_id;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span>{props.value || "NA"}</span>
              {incidentSeqId && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/Disaster-acknowledgereport/situation-awareness/incident-management", {
                      state: { highlightIncidentSeqId: incidentSeqId },
                    });
                  }}
                  title="View this incident in Incident List"
                  style={{ fontSize: "11px", color: "#2E6DA4", fontWeight: 600, cursor: "pointer", textDecoration: "underline", width: "fit-content" }}
                >
                  {incidentSeqId}
                </span>
              )}
            </div>
          );
        },
        disableSortBy: false,
      },
      {
        Header: "Severity",
        accessor: "incident_severity",
        disableSortBy: false,
        Cell: (props) =>
          props.value ? <span style={getSeverityBadgeStyle(props.value)}>{props.value}</span> : "NA",
      },
      {
        Header: "Region",
        accessor: "region_names",
        disableSortBy: false,
        Cell: (props) => props.value || "NA",
      },
      {
        Header: t("sopinstancelist.status"),
        accessor: (row) => (row.status ? t(STATUS_LABEL_MAP[row.status]) || row.status : ""),
        id: "status",
        disableSortBy: false,
        Cell: ({ row }) => {
          const value = row.original.status;
          if (!value) return t("NA");

          const label = t(STATUS_LABEL_MAP[value]) || value;
          const { dot, ...badgeStyle } = getStatusBadgeStyle(value);

          return (
            <span style={badgeStyle}>
              <span
                style={{
                  display: "inline-block",
                  width: "6px",
                  height: "6px",
                  marginRight: "6px",
                  backgroundColor: dot,
                  borderRadius: "50%",
                }}
              ></span>
              {label}
            </span>
          );
        },
           
        // cell:() =>{
        //   <div className="inactivestatus">
        //   <div id="inactive_dot"></div>
        //   <div>{t("New Task")}</div>
        // </div>
        // },

      },
//       {
//   Header: () => <div style={{ minWidth: "100px" }}>{t("sopinstancelist.createdby")}</div>,
//   accessor: (row) => {
//     const firstName = row.created_by_firstname;
//     const lastName = row.created_by_lastname;
//     return firstName && lastName ? `${firstName} ${lastName}` : firstName || "";
//   },
//   id: "created_by",
//   disableSortBy: false,
//   Cell: (props) => {
//     return <div style={{ minWidth: "100px" }}>{props.value || "NA"}</div>;
//   },
// },
      {
        Header: () => <div style={{ minWidth: "130px" }}>{t("sopinstancelist.createdon")}</div>,
        accessor: "created_on",
        disableSortBy: false,
        Cell: (props) => {
          if (props.value) {
            return (
              <div style={{ minWidth: "130px" }} title={`Time shown in ${TIMEZONE_LABEL}`}>
                <Moment format="DD-MM-YYYY, hh:mm A">{props.value}</Moment>
              </div>
            );
          } else {
            return <div style={{ minWidth: "130px" }}>NA</div>;
          }
        },
      },
      {
        Header: "Trigger Type",
        accessor: "is_auto_triggered",
        disableSortBy: true,
        Cell: ({ value }) => (
          <span style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "600",
            background: value ? "#e8f4f8" : "#eaf7f0",
            color: value ? "#1e5f8e" : "#18a3a0",
            border: `1px solid ${value ? "#b3cfe8" : "#b2dfdb"}`
          }}>
            {value ? "Auto" : "Manual"}
          </span>
        ),
      },
      {
        Header: () => <div style={{ minWidth: "90px" }}>SLA</div>,
        accessor: "sla_hours",
        disableSortBy: true,
        Cell: ({ row }) => {
          const hours = row.original.sla_hours;
          const breached = row.original.sla_breached;
          if (hours === undefined || hours === null) return "NA";
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: "90px" }}>
              <span>{hours}h</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  width: "fit-content",
                  padding: "1px 8px",
                  borderRadius: "10px",
                  fontSize: "10px",
                  fontWeight: 700,
                  background: breached ? "#FEE2E2" : "#DCFCE7",
                  color: breached ? "#B91C1C" : "#15803D",
                }}
              >
                {breached ? "Breached" : "On Track"}
              </span>
            </div>
          );
        },
      },
      {
        Header: () => <div style={{ minWidth: "140px" }}>Target Time</div>,
        accessor: "target_datetime",
        disableSortBy: true,
        Cell: ({ row }) => {
          const target = row.original.target_datetime;
          const elapsed = row.original.sla_elapsed_minutes;
          const elapsedTitle =
            elapsed !== undefined && elapsed !== null ? `Elapsed: ${Number(elapsed).toFixed(5)} min` : undefined;
          return (
            <div style={{ minWidth: "140px" }} title={elapsedTitle}>
              {target ? <Moment format="DD-MM-YYYY, hh:mm A">{target}</Moment> : "NA"}
            </div>
          );
        },
      },
      {
        Header: t("sopinstancelist.action"),
        disableSortBy: true,

        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async (id) => {
            let data = { id: id };
            var res = await putDataWithBody(deleteTaskURL, data, {});
            if (res.status == "success") {
              getTaskLists();
            }
            toast.success(res.msg, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000, // Close after 2 seconds
            });
            setIsdelete(false);
          };
 
          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() => {
                  navigate("../view-sop-instance", {
                    state: { data: props.row.original },
                  });
                }}
                title="View"
              >
                <Viewicon value={props.value} className="cursor-pointer me-2" />
              </span>
              {(() => {
                const isEditDisabled = ["Cancel", "Closed"].includes(props.row.original.status);
                return (
                  <span
                    onClick={() => {
                      if (isEditDisabled) return;
                      navigate("../sops-instance", {
                        state: {
                          data: props.row.original,
                          highlightTaskIds: highlightTaskIds,
                        },
                      });
                    }}
                    title={isEditDisabled ? "Editing is disabled for cancelled/closed SOP instances" : "Edit"}
                    style={
                      isEditDisabled
                        ? { opacity: 0.4, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    <Editicon value={props.value} className="cursor-pointer me-2" />
                  </span>
                );
              })()}
              <span
                onClick={() => setHistoryRow(props.row.original)}
                title="Audit History"
              >
                <HistoryIcon size={16} className="cursor-pointer me-2" />
              </span>

              {/* <span onClick={deletePopUp}>
                <Deleteicon
                  value={props.value}
                  className="cursor-pointer mr-1"
                />
              </span>
              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.title}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )} */}
            </div>
          );
        },
      },
    ],
    [t]
  );

  const getCreatedByName = (row) => {
    if (row.created_by_firstname && row.created_by_lastname) {
      return `${row.created_by_firstname} ${row.created_by_lastname}`;
    }
    return row.created_by_firstname || "";
  };

  // Excel/PDF export only — includes the role name alongside the full name.
  const getCreatedByExportName = (row) => {
    const fullName = getCreatedByName(row);
    if (fullName && row.created_by_role_name) {
      return `${fullName} (${row.created_by_role_name})`;
    }
    return fullName;
  };

  const statusOptions = React.useMemo(() => {
    const known = Object.keys(STATUS_LABEL_MAP);
    const extra = Array.from(
      new Set(data.map((r) => r.status).filter((s) => s && !known.includes(s)))
    ).sort();
    return [...known, ...extra];
  }, [data]);
  const severityOptions = SEVERITY_OPTIONS;
  const regionOptions = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...regionMaster,
          ...data.flatMap((r) => String(r.region_names || "").split(",").map((x) => x.trim().toUpperCase())),
        ].filter(Boolean))
      ).sort(),
    [data, regionMaster]
  );
  const filteredData = React.useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "All" && row.status !== statusFilter) return false;
      if (severityFilter !== "All" && row.incident_severity !== severityFilter) return false;
      if (
        regionFilter !== "All" &&
        !String(row.region_names || "").split(",").map((x) => x.trim().toUpperCase()).includes(regionFilter)
      ) return false;
      if (triggerTypeFilter !== "All") {
        const isAuto = !!row.is_auto_triggered;
        if (triggerTypeFilter === "Auto" && !isAuto) return false;
        if (triggerTypeFilter === "Manual" && isAuto) return false;
      }
      if (dateFrom || dateTo) {
        if (!row.created_on) return false;
        const created = new Date(row.created_on);
        if (dateFrom && created < new Date(`${dateFrom}T00:00:00`)) return false;
        if (dateTo && created > new Date(`${dateTo}T23:59:59`)) return false;
      }
      if (term) {
        // A term matching a known Region/Severity value is matched strictly
        // against that row's own region/severity field only — otherwise a
        // term like "Drass" or "Critical" can leak in via the Instance/Incident
        // Name (which is templated off the SOP name and may contain an
        // unrelated region or severity keyword) and pull in unrelated rows.
        const matchesKnownRegion = regionOptions.some((r) => r.toLowerCase().includes(term));
        const matchesKnownSeverity = severityOptions.some((s) => s.toLowerCase().includes(term));
        if (matchesKnownRegion) {
          if (!(row.region_names || "").toLowerCase().includes(term)) return false;
        } else if (matchesKnownSeverity) {
          if (!(row.incident_severity || "").toLowerCase().includes(term)) return false;
        } else {
          const haystack = [
            row.sopinstance_seq_id,
            row.name,
            row.incident,
            row.incident_seq_id,
            STATUS_LABEL_MAP[row.status] || row.status,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(term)) return false;
        }
      }
      return true;
    });
  }, [data, statusFilter, severityFilter, regionFilter, triggerTypeFilter, dateFrom, dateTo, searchText]);

  const hasActiveFilters =
    statusFilter !== "All" ||
    severityFilter !== "All" ||
    regionFilter !== "All" ||
    triggerTypeFilter !== "All" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    searchText !== "";

  const isDateRangeInvalid =
    !!pendingDateFrom && !!pendingDateTo && new Date(pendingDateFrom) > new Date(pendingDateTo);

  const handleApplyDateFilter = () => {
    if (isDateRangeInvalid) return;
    setDateFrom(pendingDateFrom);
    setDateTo(pendingDateTo);
  };

  const clearFilters = () => {
    setStatusFilter("All");
    setSeverityFilter("All");
    setRegionFilter("All");
    setTriggerTypeFilter("All");
    setPendingDateFrom("");
    setPendingDateTo("");
    setDateFrom("");
    setDateTo("");
    setSearchText("");
  };

  // Matches the "DD-MM-YYYY, hh:mm A" format the Created On column displays.
  const formatCreatedOnForExport = (value) => {
    if (!value) return "NA";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    const hours12 = d.getHours() % 12 || 12;
    const ampm = d.getHours() < 12 ? "AM" : "PM";
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}, ${pad(hours12)}:${pad(d.getMinutes())} ${ampm}`;
  };

  // "Cancelled On"/"Completed On" only appear once an instance is actually
  // cancelled/completed — only add those columns to the export when at least
  // one row in the current (filtered) data actually has the field, instead
  // of cluttering every export with two always-empty columns.
  const hasCancelledOn = filteredData.some((row) => row.cancelled_on);
  const hasCompletedOn = filteredData.some((row) => row.completed_on);
  const exportColumns = [
    ...SOP_INSTANCE_BASE_EXPORT_COLUMNS,
    ...(hasCancelledOn ? ["Cancelled On"] : []),
    ...(hasCompletedOn ? ["Completed On"] : []),
  ];

  const buildExportRows = () =>
    filteredData.map((row) => ({
      "SOP Instance ID": row.sopinstance_seq_id || "NA",
      "Instance Name": row.name || "NA",
      "Incident Name": row.incident || "NA",
      "Incident ID": row.incident_seq_id || "NA",
      Severity: row.incident_severity || "NA",
      Region: row.region_names || "NA",
      Status: row.status ? STATUS_LABEL_MAP[row.status] || row.status : "NA",
      "Created By": getCreatedByExportName(row) || "NA",
      "Created On": formatCreatedOnForExport(row.created_on),
      "Trigger Type": row.is_auto_triggered ? "Auto" : "Manual",
      "SLA Hours": row.sla_hours ?? "NA",
      "Target Datetime": formatCreatedOnForExport(row.target_datetime),
      "SLA Elapsed (min)":
        row.sla_elapsed_minutes !== undefined && row.sla_elapsed_minutes !== null
          ? Number(row.sla_elapsed_minutes).toFixed(5)
          : "NA",
      "SLA Breached": row.sla_breached ? "Yes" : "No",
      ...(hasCancelledOn ? { "Cancelled On": row.cancelled_on ? formatCreatedOnForExport(row.cancelled_on) : "NA" } : {}),
      ...(hasCompletedOn ? { "Completed On": row.completed_on ? formatCreatedOnForExport(row.completed_on) : "NA" } : {}),
    }));

  const handleExportExcel = async () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("SOP Instance List");

    const headerRow = ws.addRow(exportColumns);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E6DA4" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    rows.forEach((r) => {
      const dataRow = ws.addRow(exportColumns.map((col) => r[col]));
      dataRow.eachCell((cell) => {
        cell.alignment = { horizontal: "left", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    ws.columns.forEach((col) => {
      col.width = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `SOP_Instance_List_${exportFileStamp()}.xlsx`);
  };

  const handleExportPDF = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }
    const doc = new jsPDF("l", "pt", "a4");
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(13);
    doc.text("SOP Instance List", pageWidth / 2, 40, { align: "center" });
    doc.setFontSize(9);
    doc.text(`Generated: ${formatCreatedOnForExport(new Date())}`, pageWidth / 2, 56, {
      align: "center",
    });

    doc.autoTable({
      head: [exportColumns],
      body: rows.map((r) => exportColumns.map((c) => String(r[c] ?? ""))),
      startY: 70,
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
      headStyles: {
        fillColor: [46, 109, 164],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        fontSize: 7,
      },
      margin: { left: 20, right: 20 },
    });

    doc.save(`SOP_Instance_List_${exportFileStamp()}.pdf`);
  };

  return (
    <div>
      <div className="create-user">
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Data"
        />
        {incidentBanner && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 16px",
            borderRadius: "8px",
            border: `1px solid ${incidentBanner.found ? "#93c5fd" : "#fca5a5"}`,
            background: incidentBanner.found ? "#eff6ff" : "#fff1f2",
            color: incidentBanner.found ? "#1d4ed8" : "#dc2626",
            fontSize: "13px",
            marginBottom: "12px",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span style={{ flex: 1 }}>
              {incidentBanner.found ? "Showing SOP instances for incident:" : "No SOP instances found for incident:"}
              {" "}<strong>{incidentBanner.name}</strong>
            </span>
            <span onClick={() => setIncidentBanner(null)} style={{ cursor: "pointer", fontSize: "16px", lineHeight: 1, opacity: 0.6 }}>✕</span>
          </div>
        )}
        {matchBanner && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 16px",
            borderRadius: "8px",
            border: `1px solid ${matchBanner.found ? "#93c5fd" : "#fca5a5"}`,
            background: matchBanner.found ? "#eff6ff" : "#fff1f2",
            color: matchBanner.found ? "#1d4ed8" : "#dc2626",
            fontSize: "13px",
            marginBottom: "12px",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span style={{ flex: 1 }}>
              {matchBanner.found
                ? <>Highlighting <strong>{matchBanner.count}</strong> {matchBanner.label} record(s){matchBanner.fromIncident ? <> from incident <strong>{matchBanner.fromIncident}</strong></> : null}{matchBanner.taskHint ? <> — open a row (view action) to see the matching task(s) highlighted</> : null}</>
                : <>No matching {matchBanner.label} record(s) found{matchBanner.fromIncident ? <> for incident <strong>{matchBanner.fromIncident}</strong></> : null}</>}
            </span>
            <span onClick={() => setMatchBanner(null)} style={{ cursor: "pointer", fontSize: "16px", lineHeight: 1, opacity: 0.6 }}>✕</span>
          </div>
        )}
        <div
          style={{
            background: "#fff",
            border: "1px solid #EEF1F5",
            borderRadius: "10px",
            boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            padding: "10px 14px",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "6px",
                background: "#EBF4FA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FilterIcon size={11} color="#2E6DA4" />
            </span>
            <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
          </div>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "12px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 160px" }}>
              <label style={filterLabelStyle}>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ ...filterControlStyle, width: "100%" }}
              >
                <option value="All">Status</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL_MAP[s] || s}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 160px" }}>
              <label style={filterLabelStyle}>Severity</label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                style={{ ...filterControlStyle, width: "100%" }}
              >
                <option value="All">Severity</option>
                {severityOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 160px" }}>
              <label style={filterLabelStyle}>Region</label>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                style={{ ...filterControlStyle, width: "100%" }}
              >
                <option value="All">Region</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 150px" }}>
              <label style={filterLabelStyle}>Trigger Type</label>
              <select
                value={triggerTypeFilter}
                onChange={(e) => setTriggerTypeFilter(e.target.value)}
                style={{ ...filterControlStyle, width: "100%" }}
              >
                <option value="All">Trigger Type</option>
                <option value="Auto">Auto</option>
                <option value="Manual">Manual</option>
              </select>
            </div>
          </div>
          <style>{`
            .sop-instance-filter-bar input[type="date"] {
              position: relative;
              padding-right: 30px;
              display: inline-flex;
              align-items: center;
            }
            .sop-instance-filter-bar input[type="date"]::-webkit-datetime-edit {
              display: inline-flex;
              align-items: center;
              padding: 0;
              line-height: normal;
            }
            .sop-instance-filter-bar input[type="date"]::-webkit-datetime-edit-fields-wrapper {
              display: inline-flex;
              align-items: center;
            }
            .sop-instance-filter-bar input[type="date"]::-webkit-calendar-picker-indicator {
              position: absolute;
              right: 8px;
              top: 50%;
              transform: translateY(-50%);
              margin: 0;
              padding: 0;
              cursor: pointer;
              opacity: 0.55;
            }
            .sop-instance-filter-bar input[type="date"]::-webkit-inner-spin-button,
            .sop-instance-filter-bar input[type="date"]::-webkit-clear-button {
              display: none;
            }
          `}</style>
          <div className="sop-instance-filter-bar" style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
              <label style={filterLabelStyle}>Created From</label>
              <input
                type="date"
                value={pendingDateFrom}
                max={pendingDateTo && pendingDateTo < TODAY_STR ? pendingDateTo : TODAY_STR}
                onChange={(e) => setPendingDateFrom(clampToToday(e.target.value))}
                style={{ ...filterControlStyle, height: "34px", lineHeight: "32px", padding: "0 8px", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 160px" }}>
              <label style={filterLabelStyle}>Created To</label>
              <input
                type="date"
                value={pendingDateTo}
                min={pendingDateFrom || undefined}
                max={TODAY_STR}
                onChange={(e) => setPendingDateTo(clampToToday(e.target.value))}
                style={{ ...filterControlStyle, height: "34px", lineHeight: "32px", padding: "0 8px", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <button
              type="button"
              onClick={handleApplyDateFilter}
              disabled={!pendingDateFrom || !pendingDateTo || isDateRangeInvalid}
              title="Applies the Created From / Created To date range"
              style={{
                height: "34px", padding: "0 16px", borderRadius: "6px", fontSize: "12px", fontWeight: 600,
                border: "1px solid #2E6DA4",
                background: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "#a9c3d8" : "#2E6DA4",
                color: "#fff",
                cursor: (!pendingDateFrom || !pendingDateTo || isDateRangeInvalid) ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}
            >
              Apply
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto", color: "#98A2B3", fontSize: "11px" }}>
              <Clock size={13} />
              <span>Last Refreshed: {lastRefreshed ? formatCreatedOnForExport(lastRefreshed) : "—"}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px" }}>
              <label style={filterLabelStyle}>Search</label>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search by SOP Instance ID, Instance Name, Incident Name, Region, Severity, Status..."
                title="Search by SOP Instance ID, Instance Name, Incident Name, Region, Severity, Status..."
                style={{ ...filterControlStyle, height: "34px", lineHeight: "32px", padding: "0 10px", width: "100%", boxSizing: "border-box" }}
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  height: "34px",
                  padding: "0 12px",
                  borderRadius: "8px",
                  border: "1px solid #E4E7EC",
                  background: "#F9FAFB",
                  color: "#475467",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Clear Filters
              </button>
            )}
            <button
              type="button"
              title="Download Excel"
              style={filteredData.length ? excelExportBtnStyle : exportBtnDisabledStyle}
              onClick={handleExportExcel}
              disabled={!filteredData.length}
            >
              <Sheet size={15} />
            </button>
            <button
              type="button"
              title="Download PDF"
              style={filteredData.length ? pdfExportBtnStyle : exportBtnDisabledStyle}
              onClick={handleExportPDF}
              disabled={!filteredData.length}
            >
              <FileText size={15} />
            </button>
          </div>
          {isDateRangeInvalid && (
            <div style={{ marginTop: "8px", fontSize: "11px", color: "#DC2626", fontWeight: 600 }}>
              "Created From" date must be on or before "Created To" date.
            </div>
          )}
        </div>
        <style>{`
          .sop-instance-table tbody tr td,
          .sop-instance-table tbody tr th {
            border: none !important;
          }
          /* No !important on background-color here: rows matched via
             getRowStyle (highlightSopIds / highlightedIncidentName) set an
             inline background on the td, and inline styles already win over
             a non-important stylesheet rule of any specificity — so the
             highlight keeps working while unhighlighted rows get the
             zebra stripe below. */
          .sop-instance-table tbody tr td {
            background-color: #fff;
            color: #1D2939 !important;
          }
          .sop-instance-table tbody tr:nth-child(even) td {
            background-color: #F9FAFB;
          }
        `}</style>
        <div className="datatable__container sop-instance-table">
          <Table
            data={filteredData}
            title=""
            // addButtonLabel={t("Download")}
            // addNewHandler={addNewHandler}
            showFilter={false}
            setSearchData={setSearchData}
            columns={columns}
            numberOfRows={10}
            showExport={false}
            showReset={false}
            onExpand={false}
            showSearch={false}
            isHeaderFixed={true}
            rowCount={filteredData.length} // Pass rowCount to the Table component
            // addButtonLabel="Add New"
            // addNewHandler={addNewHandler}
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #f0f0f0",
              padding: "2px",
              borderRadius: "12px",
            }}
            getRowStyle={(row) => {
              if (highlightSopIds.length && highlightSopIds.includes(String(row.id))) {
                return { fontWeight: "700", background: "#e2e8f0", color: "#1e293b" };
              }
              if (highlightedIncidentName && row.incident === highlightedIncidentName) {
                return { fontWeight: "700", background: "#e2e8f0", color: "#1e293b" };
              }
              return undefined;
            }}
          />
        </div>
      </div>
      <SopInstanceAuditHistoryModal
        show={!!historyRow}
        onClose={() => setHistoryRow(null)}
        sopInstanceId={historyRow?.id}
        sopInstanceSeqId={historyRow?.sopinstance_seq_id}
      />
    </div>
  );
};

export default SopInstanceTable;